/**
 * CallTranscriptPanel — compact live-transcript strip inside the call popup.
 *
 * Reads from VoiceSentimentContext, which is mounted at app root and starts a
 * transcription session for EVERY Twilio call once it reaches "in-call"
 * (popup-driven calls included — the session keys off the global call state,
 * not the call-center route). This panel is therefore a pure consumer: no
 * sockets or processors of its own, it just renders whatever entries the
 * provider has collected for the current call.
 *
 * Rendered only while the call is live; terminal states keep the popup
 * compact so the end-reason copy stays the focus.
 */
import { useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "@sentry/react";
import { Sparkles } from "lucide-react";
import {
  useVoiceSentimentContext,
  type TVoiceTranscriptEntry,
} from "@/context/voice.sentiment.context";
import JumpToLatestButton from "@/components/call-center/jump.to.latest.button";
import CallMessagesPanel from "@/components/sms/call-messages-panel";
import { SMS_SENDING_ENABLED } from "@/config";
import useAutoScroll from "@/hooks/call-center/useAutoScroll";
import { cn } from "@/lib/utils";
import { useLeadSmsCount } from "@/pages/leads/lead-details/parts/use-lead-sms-count";
import PanelTab from "./PanelTab";

const speakerInitial = (speaker: string) =>
  (speaker || "?").trim().charAt(0).toUpperCase() || "?";

const CallTranscriptPanel = ({ lead }: { lead?: { id: number; name: string; avatar?: string | null } }) => {
  const { transcripts } = useVoiceSentimentContext();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Keep the newest line in view as entries stream in, paused while the agent reads back.
  const { isAutoScroll, scrollToBottom } = useAutoScroll(scrollRef, transcripts);

  // Local to this box: switching tabs only changes what is shown, never the call.
  const messagesEnabled = SMS_SENDING_ENABLED && !!lead;
  const [tab, setTab] = useState<"transcript" | "messages">("transcript");
  const [messagesOpened, setMessagesOpened] = useState(false);
  const smsCount = useLeadSmsCount(lead?.id ?? 0, messagesEnabled);

  // The transcript stays mounted while hidden; bring it to the newest line when the agent returns.
  useEffect(() => {
    if (messagesOpened && tab === "transcript") scrollToBottom();
  }, [tab, messagesOpened, scrollToBottom]);

  const showMessages = () => {
    setMessagesOpened(true);
    setTab("messages");
  };

  return (
    <div className="relative mx-5 mb-4 rounded-xl border border-white/10 bg-black/20 p-3">
      <div className={cn("mb-2 flex items-center", messagesEnabled ? "gap-2" : "gap-1.5")}>
        {messagesEnabled ? (
          <>
            <PanelTab active={tab === "transcript"} onClick={() => setTab("transcript")}>
              <Sparkles className="w-3 h-3 text-blue-300" />
              LIVE TRANSCRIPTION
            </PanelTab>
            <PanelTab active={tab === "messages"} onClick={showMessages}>
              Messages{smsCount ? ` (${smsCount})` : ""}
            </PanelTab>
          </>
        ) : (
          <>
            <Sparkles className="w-3 h-3 text-blue-300" />
            <span className="text-[10px] font-semibold tracking-wider text-white/50">
              LIVE TRANSCRIPTION
            </span>
          </>
        )}
        {tab === "transcript" && (
          <span className="ml-auto flex items-center gap-1">
            <span className="relative flex w-1.5 h-1.5">
              <span className="absolute inline-flex w-full h-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
              <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-emerald-400" />
            </span>
            <span className="text-[10px] font-semibold text-emerald-300">LIVE</span>
          </span>
        )}
      </div>

      <div className={cn("relative", tab !== "transcript" && "hidden")}>
        <div ref={scrollRef} className="max-h-[132px] overflow-y-auto pr-1 scroll-smooth">
          {transcripts.length === 0 ? (
            <p className="py-3 text-center text-xs italic text-white/40">
              Listening for speech…
            </p>
          ) : (
            <div className="space-y-1.5">
              {transcripts.map((entry: TVoiceTranscriptEntry, i) => (
                <div
                  key={entry.isFinal ? (entry.id ?? `f-${i}`) : `partial-${entry.speaker}`}
                  className={cn("flex items-start gap-2", !entry.isFinal && "opacity-60")}
                >
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-md bg-white/10 text-[9px] font-semibold text-white/80">
                    {speakerInitial(entry.speaker)}
                  </span>
                  <p className="min-w-0 text-xs leading-snug text-white/80">
                    {entry.text}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
        <JumpToLatestButton visible={!isAutoScroll} onClick={scrollToBottom} />
      </div>

      {messagesEnabled && messagesOpened && lead && (
        <div className={cn(tab !== "messages" && "hidden")}>
          <ErrorBoundary
            fallback={<p className="py-4 text-center text-[11.5px] italic text-slate-500">Messages are unavailable right now.</p>}
          >
            <CallMessagesPanel lead={lead} />
          </ErrorBoundary>
        </div>
      )}
    </div>
  );
};

export default CallTranscriptPanel;
